import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const fadeUp = {
  initial: { opacity: 0, y: 25 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.6 }
};

// ===== PARALLAX IMAGE URL =====
// Zameni sa svojim Cloudinary linkom:
const PARALLAX_IMG_1 = 'https://res.cloudinary.com/dbadhjapn/image/upload/v1773943497/hak3_ukdqvz.jpg';
const PARALLAX_IMG_2 = 'https://res.cloudinary.com/dbadhjapn/image/upload/v1773943202/hak1_ecs1mb.jpg';


const HackathonSections = () => {
  const [hideBtn, setHideBtn] = useState(false);
  const footerRef = useRef(null);

  useEffect(() => {
    const footer = document.getElementById('sponzori');
    if (!footer) return;

    const observer = new IntersectionObserver(
      ([entry]) => setHideBtn(entry.isIntersecting),
      { threshold: 0.1 }
    );

    observer.observe(footer);
    return () => observer.disconnect();
  }, []);
  return (
    <div className="content-sections">

      {/* ===== INTRO ===== */}
      <section className="section-intro">
        <motion.div className="section-intro-inner" {...fadeUp}>
          <div className="section-eyebrow">Хакатон Косовска Митровица 2030</div>
          <h2 className="section-headline">Твоја идеја може променити град</h2>
          <p className="section-lead">
            Замисли град у коме желиш да живиш. Град у коме се проблеми решавају,
            а идеје младих постају стварност. Сада замисли да баш <strong>ТИ</strong> имаш
            прилику да будеш део те промене.
          </p>
          <p className="section-lead">
            <strong>ХАКАТОН Косовска Митровица 2030</strong> је догађај који окупља
            средњошколце, студенте и младе људе који желе да својим идејама
            допринесу заједници.
          </p>
          <div className="section-divider" />
          <p className="section-lead">
            Овде није најважније да знаш да програмираш. Важно је да видиш оно
            што другима промакне. Да препознаш проблем. Да понудиш решење.
            Да верујеш да твоја идеја може учинити живот <strong>лепшим, сигурнијим
            и бољим</strong>.
          </p>
        </motion.div>
      </section>

      {/* ===== PARALLAX IMAGE 1 ===== */}
      <div className="parallax-image">
        <div
          className="parallax-image-bg"
          style={{ backgroundImage: `url(${PARALLAX_IMG_1})` }}
        />
        <motion.div
          className="parallax-image-text"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          Не тражимо програмере. Тражимо визионаре.
        </motion.div>
      </div>

      {/* ===== FOCUS AREAS ===== */}
      <section className="section-focus">
        <div className="section-focus-inner">
          <motion.h2 {...fadeUp}>Фокус хакатона</motion.h2>
          <motion.p className="section-focus-subtitle" {...fadeUp}>
            Четири стуба на којима се гради свака победничка идеја
          </motion.p>

          <div className="focus-grid">
            {[
              { icon: '💡', label: 'Креативност' },
              { icon: '🤝', label: 'Тимски рад' },
              { icon: '🔍', label: 'Проналажење\nреалних проблема' },
              { icon: '🎯', label: 'Конкретна\nрешења' },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="focus-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
              >
                <div className="focus-circle">
                  <div className="focus-circle-inner">{item.icon}</div>
                </div>
                <span className="focus-label">{item.label}</span>
              </motion.div>
            ))}
          </div>

          <motion.div className="organizer-banner" {...fadeUp}>
            Хакатон организују <a href="https://gimnazijakm.edu.rs/" target="_blank" rel="noopener noreferrer"><strong>Гимназија</strong></a>, <a href="https://ftn.pr.ac.rs/" target="_blank" rel="noopener noreferrer"><strong>ФТН</strong></a> и <a href="https://pmf.pr.ac.rs/" target="_blank" rel="noopener noreferrer"><strong>ПМФ</strong></a>,
            а ваше идеје оцењује стручна комисија састављена од остварених људи
            из индустрије који знају како се идеје претварају у стварност.
          </motion.div>
        </div>
      </section>

      {/* ===== PARALLAX DEPTH (css only, no image) ===== */}
      <div className="parallax-depth">
        <motion.div
          className="parallax-depth-text"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          Све је могуће 
        </motion.div>
      </div>

      {/* ===== STEPS ===== */}
      <section className="section-steps">
        <div className="section-steps-inner">
          <motion.h2 {...fadeUp}>Како то изгледа у пракси?</motion.h2>

          <div className="steps-list">
            {[
              { num: '1', title: 'Формираш тим', desc: 'Окупи 2–5 чланова — пријатеље, колеге.' },
              { num: '2', title: 'Добијете проблем', desc: 'Добијете или сами изаберете проблем.' },
              { num: '3', title: 'Разрађујете идеју', desc: 'План, дизајн, функционалност.' },
              { num: '4', title: 'Представљате жирију', desc: 'Презентујте свој рад стручној комисији и покажите зашто ваша идеја вреди.' },
            ].map((step, i) => (
              <motion.div
                key={i}
                className="step-item"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.5 }}
              >
                <div className="step-number">{step.num}</div>
                <div className="step-content">
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PARALLAX IMAGE 2 ===== */}
      <div className="parallax-image">
        <div
          className="parallax-image-bg"
          style={{ backgroundImage: `url(${PARALLAX_IMG_2})` }}
        />
        <motion.div
          className="parallax-image-text"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          Можда баш твоја идеја промени све.
        </motion.div>
      </div>

      {/* ===== CTA ===== */}
      <section className="section-cta">
        <motion.div className="section-cta-inner" {...fadeUp}>
          <span className="cta-icon">🚀</span>
          <h2 className="cta-title">
            Можда баш твоја идеја буде она која ће обликовати
            Косовску Митровицу какву желимо.
          </h2>
          <p className="cta-text">
            Не тражимо савршенство. Тражимо храброст да размишљаш другачије.
            Тражимо идеје које могу покренути промену.
          </p>
          <div className="cta-highlight">
            <span>📅</span> 3. и 4. април — храна и пиће обезбеђени
          </div>
          <div className="cta-rewards">
            <div className="cta-reward-item">
              <span>🏆</span>
              <p>Најуспешније тимове очекују <strong>вредне награде!</strong></p>
            </div>
            <div className="cta-reward-item">
              <span>🎁</span>
              <p>Сваки учесник добија <strong>поклон пакет!</strong></p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ===== FLOATING SPONSOR BUTTON ===== */}
      <a href="#sponzori"
        className="floating-sponsor-btn"
        style={{
          opacity: hideBtn ? 0 : 1,
          pointerEvents: hideBtn ? 'none' : 'auto',
          transition: 'opacity 0.3s ease, transform 0.3s ease',
          transform: hideBtn ? 'translateX(-50%) translateY(20px)' : 'translateX(-50%)'
        }}
      >
        <span>❤️</span> Спонзори <span>❤️</span>
      </a>
    </div>
  );
};

export default HackathonSections;